import React, { cloneElement, ReactElement, useEffect, useState } from 'react';
import { useAppSelector } from 'hooks/redux';
import cloneDeep from 'lodash/cloneDeep';
import { selectAppConfig } from 'store/appConfigSlice';
import { ElementOption, elementTypes, optionTypes } from 'types/RfiTypes';
import { Button } from '@mui/material';
import { DropDownList, DropDownListChangeEvent, ListItemProps } from '@progress/kendo-react-dropdowns';
import { Switch, SwitchChangeEvent } from '@progress/kendo-react-inputs';
import { Hint } from '@progress/kendo-react-labels/dist/npm/Hint';
import RfiElement from '../../types/RfiElement';
import ElementOptionsEditor from './ElementOptionsEditor';

export interface IFieldType {
  fieldType: string;
  label: string;
  roleRequired?: string;
  elementType?: string;
}

export interface IElementEditorProps {
  element: RfiElement;
  overrideRequired?: boolean;
  modifyElement: React.Dispatch<React.SetStateAction<RfiElement>>;
  userRole?: string;
  handleUpdate: () => Promise<void>;
  modifyElements?: (modifyAction: () => Promise<void>) => Promise<void>;
}

export const USER_ALLOW_ALL = "admin";
export const USER_ALLOW_USER = "user";
export const MAX_LENGTH_OPTION = 4000;
export const OTHER_DEFAULT_LABEL = "Other (please specify)";
// Refactor this too
export const FIELD_TYPES: IFieldType[] = [
  {
    fieldType: "sectionheader",
    label: "Section Heading",
  },
  {
    fieldType: "guidancetext",
    label: "Guidance Text",
    elementType: elementTypes.SURVEY,
    roleRequired: USER_ALLOW_USER,
  },
  {
    fieldType: "guidancetext",
    label: "Guidance Text",
    roleRequired: USER_ALLOW_ALL,
  },
  {
    fieldType: "textfield",
    label: "Single Line Text Box",
  },
  {
    fieldType: "textarea",
    label: "Multi-line Text Box",
  },
  {
    fieldType: "radiogroup",
    label: "Radio Buttons (single select)",
    elementType: elementTypes.SURVEY,
  },
  {
    fieldType: "checkbox",
    label: "Checkboxes (multi-select)",
    elementType: elementTypes.SURVEY,
  },
  {
    fieldType: "attachment",
    label: "Attachment Upload",
    elementType: elementTypes.SURVEY,
  },
  {
    fieldType: "datetime",
    label: "DateTime Picker",
    roleRequired: USER_ALLOW_ALL,
  },
  {
    fieldType: "datetime",
    label: "DateTime Picker",
    roleRequired: USER_ALLOW_USER,
    elementType: elementTypes.SURVEY,
  },
  {
    fieldType: "datepicker",
    label: "Date Picker",
    roleRequired: USER_ALLOW_ALL,
  },
  {
    fieldType: "timepicker",
    label: "Time Picker",
    roleRequired: USER_ALLOW_ALL,
  },
  {
    fieldType: "lookup",
    label: "Lookup Select",
    roleRequired: USER_ALLOW_ALL,
  },
  // {
  //   fieldType: "lookup",
  //   label: "Lookup Select",
  //   roleRequired: USER_ALLOW_USER,
  //   elementType: elementTypes.SURVEY
  // },
  // {
  //   fieldType: "lookupmultiple",
  //   label: "Lookup Multi-select",
  //   roleRequired: USER_ALLOW_USER,
  //   elementType: elementTypes.SURVEY
  // },
  {
    fieldType: "lookupmultiple",
    label: "Lookup Multi-select",
    roleRequired: USER_ALLOW_ALL,
  },

  {
    fieldType: "richtext",
    label: "Rich Text",
    roleRequired: USER_ALLOW_ALL,
  },
  {
    fieldType: "select",
    label: "Selection List",
    roleRequired: USER_ALLOW_ALL,
  },
  {
    fieldType: "selectmultiple",
    label: "Selection List Multi-select",
    roleRequired: USER_ALLOW_ALL,
  },
  {
    fieldType: "select",
    label: "Selection List",
    roleRequired: USER_ALLOW_USER,
    elementType: elementTypes.SURVEY,
  },
  {
    fieldType: "selectmultiple",
    label: "Selection List Multi-select",
    roleRequired: USER_ALLOW_USER,
    elementType: elementTypes.SURVEY,
  },
];

const ElementEditor = (props: IElementEditorProps) => {
  // Constants
  const MAX_LENGTH_FIELD_LABEL = 4000;
  const MAX_LENGTH_MAPS_TO_FIELD = 100;
  const MAX_LENGTH_LOOKUP_CODE = 500;

  // Hooks
  const appConfig = useAppSelector(selectAppConfig);

  // State
  const [labelValue, setLabelValue] = useState<string>(
    props.element.fieldLabel
  );
  const [mapsToFieldValue, setMapsToFieldValue] = useState<string>(
    props.element.mapsToField ?? ""
  );
  const [lookupCodeValue, setLookupCodeValue] = useState<string>(
    props.element.lookupCode ?? ""
  );
  const [fieldType, setFieldType] = React.useState<IFieldType | null>(
    FIELD_TYPES.find((p) => p.fieldType === props.element.fieldType) ?? null
  );
  const [elementOptions, setElementOptions] = useState<Array<ElementOption>>(
    props.element.options
      ? cloneDeep(
          props.element.options.filter(
            (o: ElementOption) => o && o.type === "default"
          )
        )
      : new Array<ElementOption>()
  );
  const [other, setOther] = useState<ElementOption | undefined>(
    (): ElementOption | undefined => {
      if (props.element.options) {
        let otherItem = props.element.options.filter(
          (o: ElementOption) => o && o.type === optionTypes.OTHER
        );

        if (otherItem && otherItem.length > 0) return cloneDeep(otherItem[0]);
      }

      return undefined;
    }
  );

  // Handlers
  const handleUpdate = async (): Promise<void> => {
    await props.handleUpdate();
  };

  const handleChangeFieldLabel = (event: React.FormEvent<HTMLInputElement>) => {
    setLabelValue(event.currentTarget.value);
    props.modifyElement({
      ...props.element,
      fieldLabel: event.currentTarget.value,
    } as RfiElement);
  };

  const handleChangeMapsToField = (
    event: React.FormEvent<HTMLInputElement>
  ) => {
    setMapsToFieldValue(event.currentTarget.value);
    props.modifyElement({
      ...props.element,
      mapsToField: event.currentTarget.value,
    } as RfiElement);
  };

  const handleChangeLookupCode = (event: React.FormEvent<HTMLInputElement>) => {
    setLookupCodeValue(event.currentTarget.value);
    props.modifyElement({
      ...props.element,
      lookupCode: event.currentTarget.value,
    } as RfiElement);
  };

  const handleChangeFieldType = (event: DropDownListChangeEvent) => {
    setFieldType(event.target.value);
    props.modifyElement({
      ...props.element,
      fieldType: event.target?.value?.fieldType ?? "",
    } as RfiElement);
  };
  // Handlers - Required
  const handleChangeResponseRequired = (event: SwitchChangeEvent) => {
    props.modifyElement({
      ...props.element,
      requiredResponse: event.target.value,
    } as RfiElement);
  };
  const handleChangeSystemRequired = (event: SwitchChangeEvent) => {
    if (props.userRole === USER_ALLOW_ALL) {
      props.modifyElement({
        ...props.element,
        requiredInRfi: event.target.value,
      } as RfiElement);
    } else {
      //console.log("Insufficient permissions to change RfiElement's System Required");
    }
  };
  const handleChangeCustomerRequired = (event: SwitchChangeEvent) => {
    if (props.userRole === USER_ALLOW_ALL) {
      props.modifyElement({
        ...props.element,
        customerRequired: event.target.value,
      } as RfiElement);
    } else {
      //console.log("Insufficient permissions to change RfiElement's Customer Required");
    }
  };

  const handleChangeEditLabel = (event: SwitchChangeEvent) => {
    if (props.userRole === USER_ALLOW_ALL) {
      props.modifyElement({
        ...props.element,
        editLabel: event.target.value,
      } as RfiElement);
    } else {
      //console.log("Insufficient permissions to change RfiElement's Edit Label");
    }
  };

  const handleChangeEditRequired = (event: SwitchChangeEvent) => {
    if (props.userRole === USER_ALLOW_ALL) {
      props.modifyElement({
        ...props.element,
        editRequired: event.target.value,
      } as RfiElement);
    } else {
      //console.log("Insufficient permissions to change RfiElement's Edit Required");
    }
  };

  // Handlers - Options
  const handleChangeOptions = (options: Array<ElementOption>) => {
    setElementOptions(options);
    // Parent modified with useEffect
  };

  const handleEnableDisableOther = (event: SwitchChangeEvent) => {
    // Remove Other
    if (other !== undefined) {
      setOther(undefined);
    }
    // Add an Other field
    else {
      const otherOption: ElementOption = {
        value: undefined,
        description: "",
        sequence: 0,
        type: optionTypes.OTHER,
      };

      setOther(otherOption);
    }
  };

  // Helper Functions
  const mergeElementOptions = (
    newOptions: Array<ElementOption>,
    newOther: ElementOption | undefined
  ): Array<ElementOption> => {
    let result = new Array<ElementOption>();

    if (newOptions) {
      newOptions.forEach((option: ElementOption, index: number) => {
        // Update Sequences
        option.sequence = index + 1;
        if (option?.description) {
          result.push(option);
        }
      });
    }

    if (newOther !== undefined) {
      result.push(newOther);
    }

    return result;
  };

  // Effects
  useEffect(() => {
    // Merge Options & Other for parent
    props.modifyElement({
      ...props.element,
      options: mergeElementOptions(elementOptions, other),
    } as RfiElement);
  }, [elementOptions, other]);

  // Render
  // Differentiate current element
  const fieldTypeItemRender = (
    li: ReactElement<HTMLLIElement>,
    itemProps: ListItemProps
  ) => {
    let itemChildren = (
      <span>
        <>{li.props.children}</>
      </span>
    );

    return cloneElement(li, li.props, itemChildren);
  };

  return (
    <>
      {/* Field Type/Label */}
      <div className="container my-2">
        <div className="form-group row">
          <label
            htmlFor={`field-label-${props.element.elementId}`}
            className="col-lg-1 col-form-label"
          >
            Label:
          </label>
          <div className="col-lg-8 fieldElement">
            <input
              type="text"
              id={`field-label-${props.element.elementId}`}
              className="form-control"
              onChange={handleChangeFieldLabel}
              value={labelValue}
              maxLength={
                appConfig.maxFieldLabelLength ?? MAX_LENGTH_FIELD_LABEL
              }
              disabled={!props.element.editLabel}
            />
            <Hint direction={"end"}>
              {labelValue?.length ?? 0} /{" "}
              {appConfig.maxFieldLabelLength ?? MAX_LENGTH_FIELD_LABEL}
            </Hint>
          </div>
          <div className="col-lg-3">
            <DropDownList
              style={{ minHeight: "40px" }}
              ariaLabel="Select Type Dropdown"
              data={FIELD_TYPES.filter((ft: IFieldType) => {
                if (props.userRole) {
                  return (
                    ft.roleRequired === undefined ||
                    props.userRole === ft.roleRequired
                  );
                } else {
                  return ft.roleRequired === undefined;
                }
              }).filter((ft: IFieldType) => {
                if (props.userRole !== USER_ALLOW_ALL) {
                  return (
                    ft.elementType === undefined ||
                    props.element.elementType === ft.elementType
                  );
                } else {
                  return true;
                }
              })}
              disabled={
                props.overrideRequired ||
                props.element.requiredInRfi ||
                props.element.customerRequired
              }
              dataItemKey="fieldType"
              textField="label"
              value={fieldType}
              itemRender={fieldTypeItemRender}
              onChange={handleChangeFieldType}
            />
          </div>
        </div>
      </div>

      {/* ElementOptions Editor */}
      {elementOptions &&
        fieldType &&
        (fieldType?.fieldType === "radiogroup" ||
          fieldType?.fieldType === "checkbox" ||
          fieldType?.fieldType === "select" ||
          fieldType?.fieldType === "selectmultiple" ||
          fieldType?.fieldType === "dropdown") && (
          <ElementOptionsEditor
            elementId={props.element.elementId}
            options={elementOptions}
            setOptions={handleChangeOptions}
            disabled={
              props.overrideRequired ||
              props.element.requiredInRfi ||
              props.element.customerRequired
                ? true
                : false
            }
          />
        )}

      {/* Other*/}
      {fieldType &&
        (fieldType?.fieldType === "radiogroup" ||
          fieldType?.fieldType === "checkbox") && (
          <div className="container my-2">
            <div className="d-flex flex-row flex-nowrap align-items-center">
              <div className="col-1" style={{ minWidth: "80px" }}>
                <Switch
                  id={`other-switch-${props.element.elementId}`}
                  defaultChecked={other !== undefined}
                  value={other !== undefined}
                  onChange={handleEnableDisableOther}
                  name={"Enable/Disable Other"}
                  aria-label="enable or disable the other text box for this question"
                  disabled={
                    props.overrideRequired ||
                    props.element.requiredInRfi ||
                    props.element.customerRequired
                      ? true
                      : false
                  }
                />
              </div>
              <label
                htmlFor={`other-switch-${props.element.elementId}`}
                className="col-form-label col"
              >
                Other Text Box
              </label>
              {!other && <div className="col-7"></div>}
              {other && (
                <>
                  <label
                    htmlFor={`options-other-custom-${props.element.elementId}`}
                    className="col-form-label col-2"
                    style={{ textAlign: "center" }}
                  >
                    Custom Prompt:
                  </label>
                  <div className="col-4">
                    <input
                      id={`options-other-custom-${props.element.elementId}`}
                      type="text"
                      className={`form-control col-4 options-other-custom-${props.element.elementId}`}
                      value={other?.description ?? ""}
                      maxLength={appConfig.maxOptionLength ?? MAX_LENGTH_OPTION}
                      placeholder={OTHER_DEFAULT_LABEL}
                      aria-label={`enter a custom prompt for the other text box, leave blank for ${OTHER_DEFAULT_LABEL}`}
                      // minLength={1}
                      // required
                      onChange={(event: React.FormEvent<HTMLInputElement>) => {
                        setOther({
                          ...other,
                          description: event?.currentTarget?.value ?? "",
                        });
                      }}
                      disabled={
                        props.overrideRequired ||
                        props.element.requiredInRfi ||
                        props.element.customerRequired
                          ? true
                          : false
                      }
                    />
                  </div>

                  <Hint direction={"end"} className="col-1 align-items-center">
                    {other?.description?.length ? other.description.length : 0}{" "}
                    / {appConfig.maxOptionLength ?? MAX_LENGTH_OPTION}
                  </Hint>
                </>
              )}
              <div className="col-1" />
            </div>
          </div>
        )}

      {/* Required Elements */}
      {fieldType &&
        !(
          fieldType?.fieldType === "sectionheader" ||
          fieldType?.fieldType === "guidancetext"
        ) &&
        (props.userRole === USER_ALLOW_ALL ||
          props.element?.editRequired === true) && (
          <div className="container my-2" style={{ minHeight: "60px" }}>
            <div className="d-flex flex-row flex-nowrap align-items-center">
              <div className="col-1" style={{ minWidth: "80px" }}>
                <Switch
                  id={`response-required-${props.element.elementId}`}
                  defaultChecked={props.element.requiredResponse ?? false}
                  value={props.element.requiredResponse}
                  onChange={handleChangeResponseRequired}
                  name={"Require an answer"}
                  aria-label="Enable or disable requiring a value to be submitted"
                />
              </div>
              <label
                htmlFor={`response-required-${props.element.elementId}`}
                className="col-form-label col"
              >
                Require an answer to this element
              </label>
            </div>
          </div>
        )}
      {/* Admin Only */}
      {props.userRole === USER_ALLOW_ALL && (
        <div className="container my-2" style={{ minHeight: "60px" }}>
          <div className="d-flex flex-row flex-nowrap align-items-center">
            <p className="col-lg-1 col-form-label">Admin:</p>
          </div>
          <div className="d-flex flex-row flex-nowrap align-items-center">
            <div className="col-1" style={{ minWidth: "80px" }}>
              <Switch
                id={`system-required-${props.element.elementId}`}
                defaultChecked={props.element.requiredInRfi ?? false}
                value={props.element.requiredInRfi}
                onChange={handleChangeSystemRequired}
                name={"Required by System"}
                aria-label="Enable or disable the System requiring this element on the Rfi"
              />
            </div>
            <label
              htmlFor={`system-required-${props.element.elementId}`}
              className="col-form-label col"
            >
              Required on RFI by System
            </label>
          </div>
          <div className="d-flex flex-row flex-nowrap align-items-center">
            <div className="col-1" style={{ minWidth: "80px" }}>
              <Switch
                id={`customer-required-${props.element.elementId}`}
                defaultChecked={props.element?.customerRequired ?? false}
                value={props.element.customerRequired}
                onChange={handleChangeCustomerRequired}
                name={"Required by Customer"}
                aria-label="Enable or disable the Customer requiring this element on the Rfi"
              />
            </div>
            <label
              htmlFor={`customer-required-${props.element.elementId}`}
              className="col-form-label col"
            >
              Required on RFI by Customer
            </label>
          </div>
          <div className="d-flex flex-row flex-nowrap align-items-center">
            <div className="col-1" style={{ minWidth: "80px" }}>
              <Switch
                id={`edit-label-${props.element.elementId}`}
                defaultChecked={props.element?.editLabel ?? true}
                value={props.element.editLabel}
                onChange={handleChangeEditLabel}
                name={"Edit Label"}
                aria-label="Enable or disable editing the label of this element"
              />
            </div>
            <label
              htmlFor={`edit-label-${props.element.elementId}`}
              className="col-form-label col"
            >
              User can edit label
            </label>
          </div>
          <div className="d-flex flex-row flex-nowrap align-items-center">
            <div className="col-1" style={{ minWidth: "80px" }}>
              <Switch
                id={`edit-required-${props.element.elementId}`}
                defaultChecked={props.element?.editRequired ?? true}
                value={props.element.editRequired}
                onChange={handleChangeEditRequired}
                name={"Edit Required"}
                aria-label="Enable or disable changing whether or not this element requires an answer"
              />
            </div>
            <label
              htmlFor={`edit-required-${props.element.elementId}`}
              className="col-form-label col"
            >
              User can require an answer
            </label>
          </div>
          <div className="form-group row">
            <label
              htmlFor={`map-to-name-${props.element.elementId}`}
              className="col-lg-1 col-form-label"
            >
              Maps To Name:
            </label>
            <div className="col-lg-8 fieldElement">
              <input
                type="text"
                id={`map-to-name-${props.element.elementId}`}
                className="form-control"
                onChange={handleChangeMapsToField}
                value={mapsToFieldValue}
                maxLength={
                  appConfig.maxMapsToFieldLength ?? MAX_LENGTH_MAPS_TO_FIELD
                }
              />
              <Hint direction={"end"}>
                {mapsToFieldValue?.length ?? 0} /{" "}
                {appConfig.maxMapsToFieldLength ?? MAX_LENGTH_MAPS_TO_FIELD}
              </Hint>
            </div>
          </div>
          <div className="form-group row">
            <label
              htmlFor={`lookup-code-${props.element.elementId}`}
              className="col-lg-1 col-form-label"
            >
              Lookup Code:
            </label>
            <div className="col-lg-8 fieldElement">
              <input
                type="text"
                id={`lookup-code-${props.element.elementId}`}
                className="form-control"
                onChange={handleChangeLookupCode}
                value={lookupCodeValue}
                maxLength={
                  appConfig.maxLookupCodeLength ?? MAX_LENGTH_LOOKUP_CODE
                }
              />
              <Hint direction={"end"}>
                {lookupCodeValue?.length ?? 0} /{" "}
                {appConfig.maxLookupCodeLength ?? MAX_LENGTH_LOOKUP_CODE}
              </Hint>
            </div>
          </div>
        </div>
      )}

      {/* Update Button */}
      <div className="container my-2">
        <Button variant="contained" onClick={handleUpdate}>
          Update Element
        </Button>
      </div>
    </>
  );
};

export default ElementEditor;
